import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface Transformation {
  name: string;
  duration: string;
  package: string;
  packageColor: string;
  weightLost: string;
  stat2Label: string;
  stat2Value: string;
  quote: string;
  realPhoto: string;
}

const transformations: Transformation[] = [
  {
    name: 'Legacy Family Yara',
    duration: '16 Weeks',
    package: 'Foundation',
    packageColor: '#B8860B',
    weightLost: 'Full Transformation',
    stat2Label: 'Experience',
    stat2Value: 'Life-Changing',
    quote: 'I was referred to Benny and had never worked out in a gym before. He was incredibly professional, patient, and made me feel comfortable from day one.',
    realPhoto: 'https://img1.wsimg.com/isteam/ip/79f03f3e-096e-49e4-9086-49ca6ab90ae6/Screenshot%202026-01-03%20at%209.48.05%E2%80%AFPM.png',
  },
  {
    name: 'Legacy Family David',
    duration: '12 Weeks',
    package: 'Ascend',
    packageColor: '#C8102E',
    weightLost: '90+ lbs',
    stat2Label: 'Mindset',
    stat2Value: 'Completely Transformed',
    quote: 'I lost over 90 pounds and completely transformed my mindset through the Legacy Family program. The structure, accountability, and genuine care made all the difference.',
    realPhoto: 'https://img1.wsimg.com/isteam/ip/79f03f3e-096e-49e4-9086-49ca6ab90ae6/IMG-3355.jpg',
  },
  {
    name: 'Legacy Family Kevin',
    duration: '8 Weeks',
    package: 'Legacy Elite',
    packageColor: '#1a1a1a',
    weightLost: 'Elite Results',
    stat2Label: 'Approach',
    stat2Value: 'Fitness & Nutrition',
    quote: 'I saw Benny on Instagram and thought this guy is serious! I reached out and he completely changed my approach to fitness and nutrition. Best decision I ever made.',
    realPhoto: 'https://img1.wsimg.com/isteam/ip/79f03f3e-096e-49e4-9086-49ca6ab90ae6/8AF7DBC5-07A5-4FAA-B2FD-62730B8A955B.JPG',
  },
];

const stats = [
  { value: '500+', label: 'Lives Transformed' },
  { value: '94%', label: 'Client Retention Rate' },
  { value: '30 lbs', label: 'Avg. Weight Lost' },
  { value: '4.9★', label: 'Average Rating' },
];

interface TransformationCardProps {
  t: Transformation;
  delay: number;
}

function TransformationCard({ t, delay }: TransformationCardProps) {
  const { elementRef } = useScrollAnimation({ delay });
  return (
    <div
      ref={elementRef}
      className="animate-fade-up flex flex-col rounded-2xl overflow-hidden"
      style={{ background: '#ffffff', border: '1px solid #ebebeb', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
    >
      <div className="relative w-full" style={{ height: '340px' }}>
        <img
          src={t.realPhoto}
          alt={`${t.name} transformation result`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 50%)' }} />
        <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase" style={{ background: '#B8860B', color: '#000000' }}>
          RESULT
        </div>
        <div className="absolute bottom-4 left-4 px-3 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(0,0,0,0.65)', color: '#F5D76E' }}>
          {t.duration}
        </div>
      </div>

      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-extrabold text-gray-900">{t.name}</h3>
            <span
              className="text-xs font-bold tracking-widest uppercase px-2 py-0.5 rounded-full"
              style={{
                background: `${t.packageColor}18`,
                color: t.packageColor === '#1a1a1a' ? '#555555' : t.packageColor,
                border: `1px solid ${t.packageColor === '#1a1a1a' ? '#cccccc' : t.packageColor + '44'}`,
              }}
            >
              {t.package}
            </span>
          </div>
          <div className="text-right">
            <p className="text-lg font-black leading-tight" style={{ color: '#B8860B' }}>{t.weightLost}</p>
            <p className="text-xs" style={{ color: '#888888' }}>achieved</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-5 px-3 py-2 rounded-lg" style={{ background: '#fafafa', border: '1px solid #f0e0b0' }}>
          <i className="ri-bar-chart-2-line text-sm" style={{ color: '#B8860B' }}></i>
          <span className="text-xs font-medium" style={{ color: '#888888' }}>{t.stat2Label}:</span>
          <span className="text-xs font-bold text-gray-800">{t.stat2Value}</span>
        </div>

        <blockquote className="text-sm leading-relaxed italic flex-1" style={{ color: '#555555' }}>
          <i className="ri-double-quotes-l text-lg mr-1" style={{ color: '#B8860B' }}></i>
          {t.quote}
        </blockquote>

        <div className="flex gap-1 mt-4">
          {[...Array(5)].map((_, i) => (
            <i key={i} className="ri-star-fill text-sm" style={{ color: '#F5D76E' }}></i>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Transformations() {
  const { elementRef: headerRef } = useScrollAnimation();
  const { elementRef: statsBarRef } = useScrollAnimation({ delay: 100 });
  const { elementRef: ctaRef } = useScrollAnimation();

  return (
    <section id="transformations" className="py-20 sm:py-28" style={{ background: '#fafafa' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div ref={headerRef} className="animate-fade-up text-center mb-14 sm:mb-20">
          <p className="text-xs font-bold tracking-[0.35em] uppercase mb-3" style={{ color: '#B8860B' }}>
            Real Results. Real People.
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-5 leading-tight text-gray-900">
            Success Stories
          </h2>
          <p className="text-sm sm:text-base max-w-xl mx-auto" style={{ color: '#666666' }}>
            These are not stock photos. These are Legacy Family members who committed, showed up, and changed their lives.
          </p>
          <div className="w-16 h-1 mx-auto rounded-full mt-6" style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)' }} />
        </div>

        <div ref={statsBarRef} className="animate-fade-up grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16 sm:mb-20">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className="rounded-xl py-6 px-4 text-center"
              style={{ background: '#ffffff', border: '1px solid #f0e0b0', boxShadow: '0 2px 12px rgba(184,134,11,0.08)' }}
            >
              <p className="text-2xl sm:text-3xl font-black mb-1" style={{ color: '#B8860B' }}>{stat.value}</p>
              <p className="text-xs font-medium tracking-wide uppercase" style={{ color: '#888888' }}>{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {transformations.map((t, idx) => (
            <TransformationCard key={idx} t={t} delay={idx * 150} />
          ))}
        </div>

        <div ref={ctaRef} className="animate-fade-up text-center mt-16">
          <p className="text-base font-medium mb-6" style={{ color: '#666666' }}>
            Ready to write your own story?
          </p>
          <a
            href="#booking"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap hover:opacity-90 hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)', color: '#000000' }}
          >
            <i className="ri-trophy-line text-base"></i>
            Start Your Transformation
          </a>
        </div>
      </div>
    </section>
  );
}
